import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { PullToRefreshContext } from './PullToRefreshContext';

// Pull-to-refresh in stile app: swipe verso il basso dalla cima della pagina per ricaricare.
// SOLO su app nativa (gate Capacitor.isNativePlatform): in web il gesto non esiste e non
// interferisce con lo scroll del browser. L'app scrolla la finestra (nessun contenitore
// interno con overflow), quindi il gesto parte solo quando window.scrollY è a 0.
//
// Animazione in stile browser (revisione 2026-07-21): il pull SPINGE IN GIÙ il contenuto
// creando spazio in cima, e lo spinner "nudo" (niente box galleggiante) vive centrato in
// quello spazio, sullo sfondo. Implementato con padding-top animato sul wrapper del
// contenuto, NON con transform: questo componente avvolge tutta l'app, inclusi elementi
// position:fixed (BaseBar, StatusBarBackdrop, overlay) — un antenato con transform
// diventerebbe il loro containing block e li sposterebbe (la BaseBar finirebbe in fondo
// al documento). Il padding muove solo il flusso e lascia i fixed al loro posto.
//
// Le pagine registrano il proprio refetch via useRefreshHandler; quelle che non registrano
// nulla ricadono su un reload completo. Lo spinner resta almeno MIN_SPINNER_MS per dare
// feedback anche quando il refetch è istantaneo.

const PULL_THRESHOLD = 70;   // px di trascinamento (già con resistenza) per far scattare il refresh
const MAX_PULL = 110;        // trascinamento massimo dell'indicatore
const RESISTANCE = 0.5;      // "durezza" del pull: metà del movimento del dito
const MIN_SPINNER_MS = 500;

const PullToRefresh = ({ children }) => {
    const isNative = Capacitor.isNativePlatform();

    // Handler della pagina corrente (registry a slot singolo)
    const handlerRef = useRef(null);
    const registry = useMemo(() => ({
        register: (fn) => { handlerRef.current = fn; },
        unregister: (fn) => { if (handlerRef.current === fn) handlerRef.current = null; },
    }), []);

    // Stato del gesto: ref per la logica (nessun re-render), state per l'indicatore
    const startY = useRef(0);
    const startX = useRef(0);
    const pullingRef = useRef(false);
    const pullRef = useRef(0);
    const refreshingRef = useRef(false);
    const [pull, setPullState] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const setPull = useCallback((value) => {
        pullRef.current = value;
        setPullState(value);
    }, []);

    const runRefresh = useCallback(async () => {
        refreshingRef.current = true;
        setRefreshing(true);
        setPull(PULL_THRESHOLD);
        const started = Date.now();
        try {
            const result = handlerRef.current ? handlerRef.current() : window.location.reload();
            if (result && typeof result.then === 'function') await result;
        } catch (error) {
            console.error('Pull-to-refresh: errore nel refresh della pagina:', error);
        }
        const elapsed = Date.now() - started;
        if (elapsed < MIN_SPINNER_MS) {
            await new Promise(resolve => setTimeout(resolve, MIN_SPINNER_MS - elapsed));
        }
        refreshingRef.current = false;
        setRefreshing(false);
        setPull(0);
    }, [setPull]);

    useEffect(() => {
        if (!isNative) return undefined;

        const onTouchStart = (event) => {
            if (refreshingRef.current || window.scrollY > 0) {
                pullingRef.current = false;
                return;
            }
            startY.current = event.touches[0].clientY;
            startX.current = event.touches[0].clientX;
            pullingRef.current = true;
        };

        const onTouchMove = (event) => {
            if (!pullingRef.current || refreshingRef.current) return;
            const dy = event.touches[0].clientY - startY.current;
            const dx = event.touches[0].clientX - startX.current;
            // Gesto orizzontale (es. caroselli): non è un pull → annullo
            if (Math.abs(dx) > Math.abs(dy)) {
                pullingRef.current = false;
                if (pullRef.current !== 0) setPull(0);
                return;
            }
            // Se nel frattempo la pagina si è mossa dalla cima, o si trascina verso l'alto, annullo
            if (dy <= 0 || window.scrollY > 0) {
                if (pullRef.current !== 0) setPull(0);
                return;
            }
            setPull(Math.min(dy * RESISTANCE, MAX_PULL));
        };

        const onTouchEnd = () => {
            if (!pullingRef.current || refreshingRef.current) return;
            pullingRef.current = false;
            if (pullRef.current >= PULL_THRESHOLD) {
                runRefresh();
            } else if (pullRef.current !== 0) {
                setPull(0);
            }
        };

        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: true });
        window.addEventListener('touchend', onTouchEnd, { passive: true });
        window.addEventListener('touchcancel', onTouchEnd, { passive: true });
        return () => {
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
            window.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [isNative, runRefresh, setPull]);

    const progress = Math.min(pull / PULL_THRESHOLD, 1);
    // Spazio creato in cima: segue il dito durante il pull (nessuna transizione),
    // si assesta a PULL_THRESHOLD durante il refresh, torna a 0 (animato) alla fine.
    const gap = refreshing ? PULL_THRESHOLD : pull;
    const isDragging = pullingRef.current;

    return (
        <PullToRefreshContext.Provider value={registry}>
            {
                isNative && gap > 0 &&
                // Lo spinner vive nello spazio rivelato (sotto la safe area), centrato:
                // il contenitore è alto esattamente quanto il gap, quindi non si sovrappone
                // mai al contenuto che scende. Niente box: solo l'anello sullo sfondo.
                <div
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        top: 'env(safe-area-inset-top, 0px)',
                        left: 0,
                        right: 0,
                        height: `${gap}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        pointerEvents: 'none',
                        transition: isDragging ? 'none' : 'height 0.25s ease',
                    }}
                >
                    <div
                        className={`w-6 h-6 rounded-full border-[3px] border-[#225424] border-t-transparent ${refreshing ? 'animate-spin' : ''}`}
                        style={refreshing ? undefined : { opacity: progress, transform: `rotate(${progress * 270}deg)` }}
                    ></div>
                </div>
            }
            {/* Il padding-top spinge in giù tutto il flusso rivelando lo sfondo (effetto
                "margine che si apre" dei pull-to-refresh browser); gli elementi fixed
                (BaseBar, backdrop, overlay) non ne sono toccati. */}
            <div style={isNative ? { paddingTop: `${gap}px`, transition: isDragging ? 'none' : 'padding-top 0.25s ease' } : undefined}>
                {children}
            </div>
        </PullToRefreshContext.Provider>
    );
};

export default PullToRefresh;
